import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Search,
  Edit2,
  Trash2,
  TestTube,
  X,
  Save,
  FileJson,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Use cookie-based auth - credentials: 'include' sends session cookie
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

// Helper to make authenticated fetch requests
const authFetch = (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });
};

const AdminMappingProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    name: '',
    source_kind: 'csv',
    mappings: '{}',
    required_source_fields: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Test modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testProfile, setTestProfile] = useState(null);
  const [testInput, setTestInput] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const fetchVendors = async () => {
    try {
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/vendors`);
      if (resp.ok) {
        const data = await resp.json();
        setVendors(data.vendors || []);
      }
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchProfiles = useCallback(async () => {
    if (!selectedVendor) {
      setProfiles([]);
      return;
    }
    
    try {
      setLoading(true);
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/vendors/${selectedVendor}/mapping-profiles`);
      
      if (!resp.ok) throw new Error('Failed to fetch profiles');
      
      const data = await resp.json();
      setProfiles(data.profiles || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedVendor]);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleOpenModal = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        vendor_id: profile.vendor_id,
        name: profile.name,
        source_kind: profile.source_kind,
        mappings: JSON.stringify(profile.mappings, null, 2),
        required_source_fields: profile.required_source_fields?.join(', ') || ''
      });
    } else {
      setEditingProfile(null);
      setFormData({
        vendor_id: selectedVendor || '',
        name: '',
        source_kind: 'csv',
        mappings: JSON.stringify({
          vendor_sku: '',
          title: '',
          description: '',
          cost_price: '',
          msrp: '',
          image_urls: '',
          product_type_id: '',
          category_id: ''
        }, null, 2),
        required_source_fields: ''
      });
    }
    setShowModal(true);
    setError(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProfile(null);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Parse mappings JSON
      let mappings;
      try {
        mappings = JSON.parse(formData.mappings);
      } catch (e) {
        throw new Error('Invalid JSON in mappings field');
      }
      
      const body = {
        vendor_id: formData.vendor_id,
        name: formData.name,
        source_kind: formData.source_kind,
        mappings,
        required_source_fields: formData.required_source_fields
          ? formData.required_source_fields.split(',').map(s => s.trim()).filter(Boolean)
          : []
      };
      
      let resp;
      if (editingProfile) {
        resp = await authFetch(`${API_URL}/api/catalog-imports/admin/mapping-profiles/${editingProfile.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body)
        });
      } else {
        resp = await authFetch(`${API_URL}/api/catalog-imports/admin/vendors/${formData.vendor_id}/mapping-profiles`, {
          method: 'POST',
          body: JSON.stringify(body)
        });
      }
      
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.detail || 'Failed to save profile');
      }
      
      handleCloseModal();
      fetchProfiles();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Delete profile "${profile.name}"? This cannot be undone.`)) return;
    
    try {
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/mapping-profiles/${profile.id}`, {
        method: 'DELETE'
      });
      
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.detail || 'Failed to delete profile');
      }
      
      fetchProfiles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      
      let sampleRow;
      try {
        sampleRow = JSON.parse(testInput);
      } catch (e) {
        throw new Error('Invalid JSON in sample row');
      }
      
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/mapping-profiles/${testProfile.id}/test`, {
        method: 'POST',
        body: JSON.stringify(sampleRow)
      });
      
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.detail || 'Test failed');
      }
      
      const data = await resp.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const openTestModal = (profile) => {
    setTestProfile(profile);
    setTestInput(JSON.stringify({
      "STYLE": "TEST001",
      "PRODUCT_TITLE": "Test Product",
      "DESCRIPTION": "A test product description",
      "PIECE_PRICE": "9.99",
      "RETAIL_PRICE": "24.99",
      "IMAGE_URL": "https://example.com/image.jpg"
    }, null, 2));
    setTestResult(null);
    setShowTestModal(true);
  };

  return (
    <div className="space-y-6" data-testid="mapping-profiles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-ivory-800">Mapping Profiles</h2>
          <p className="text-sm text-ivory-500 font-body">Configure vendor column mappings</p>
        </div>
        <Button onClick={() => handleOpenModal()} disabled={!selectedVendor} data-testid="add-profile-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Profile
        </Button>
      </div>

      {/* Vendor Selector */}
      <div className="bg-white rounded-xl border border-ivory-200 p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-ivory-700">Select Vendor:</label>
          <select
            className="flex-1 max-w-xs px-3 py-2 border border-ivory-300 rounded-lg"
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
          >
            <option value="">-- Select a vendor --</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Profiles Table */}
      {selectedVendor && (
        <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-ivory-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Profile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Source Kind</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Mappings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Required Fields</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ivory-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gold-500" />
                  </td>
                </tr>
              ) : profiles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-ivory-500">
                    No mapping profiles found for this vendor
                  </td>
                </tr>
              ) : (
                profiles.map((profile) => (
                  <tr key={profile.id} className="hover:bg-ivory-50" data-testid={`profile-row-${profile.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-ivory-800">{profile.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        {profile.source_kind?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-ivory-600">
                      {Object.keys(profile.mappings || {}).length} fields
                    </td>
                    <td className="px-4 py-3 text-sm text-ivory-500">
                      {profile.required_source_fields?.join(', ') || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openTestModal(profile)} title="Test mapping">
                          <TestTube className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenModal(profile)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(profile)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!selectedVendor && (
        <div className="bg-ivory-50 rounded-xl border border-ivory-200 p-8 text-center text-ivory-500">
          Select a vendor to view and manage mapping profiles
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-ivory-200 flex items-center justify-between">
              <h3 className="font-semibold text-ivory-800">
                {editingProfile ? 'Edit Mapping Profile' : 'Add Mapping Profile'}
              </h3>
              <Button variant="ghost" size="sm" onClick={handleCloseModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-1">Vendor *</label>
                  <select
                    className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                    value={formData.vendor_id}
                    onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                    disabled={!!editingProfile}
                  >
                    <option value="">Select vendor...</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-1">Source Kind *</label>
                  <select
                    className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                    value={formData.source_kind}
                    onChange={(e) => setFormData({ ...formData, source_kind: e.target.value })}
                  >
                    <option value="csv">CSV</option>
                    <option value="xlsx">XLSX</option>
                    <option value="api">API</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">Profile Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., SanMar CSV Default"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Mappings (JSON) *
                </label>
                <p className="text-xs text-ivory-500 mb-2">
                  Map canonical field names to source column names. Format: {`{"target_field": "source_column"}`}
                </p>
                <textarea
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg font-mono text-sm"
                  rows={10}
                  value={formData.mappings}
                  onChange={(e) => setFormData({ ...formData, mappings: e.target.value })}
                  placeholder='{"vendor_sku": "STYLE", "title": "PRODUCT_TITLE"}'
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Required Source Fields
                </label>
                <Input
                  value={formData.required_source_fields}
                  onChange={(e) => setFormData({ ...formData, required_source_fields: e.target.value })}
                  placeholder="Comma-separated, e.g., STYLE, PRODUCT_TITLE"
                />
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-ivory-200 flex justify-end gap-3">
              <Button variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !formData.name || !formData.vendor_id}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-ivory-200 flex items-center justify-between">
              <h3 className="font-semibold text-ivory-800 flex items-center gap-2">
                <TestTube className="w-5 h-5" />
                Test Mapping: {testProfile?.name}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowTestModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">
                  Sample Row (JSON)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg font-mono text-sm"
                  rows={8}
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                />
              </div>
              
              <Button onClick={handleTest} disabled={testing}>
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                Run Test
              </Button>
              
              {testResult && (
                <div className="space-y-4">
                  {testResult.error ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {testResult.error}
                    </div>
                  ) : (
                    <>
                      <div className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
                        testResult.validation?.status === 'valid' 
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : testResult.validation?.status === 'warning'
                          ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
                          : 'bg-red-50 border border-red-200 text-red-700'
                      }`}>
                        {testResult.validation?.status === 'valid' ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                        Validation Status: {testResult.validation?.status}
                      </div>
                      
                      {testResult.validation?.errors?.length > 0 && (
                        <div className="space-y-1">
                          {testResult.validation.errors.map((err, idx) => (
                            <div key={idx} className="text-sm text-red-600">
                              • {err.code}: {err.message}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-ivory-700 mb-1">Raw Input</label>
                          <pre className="bg-ivory-50 p-3 rounded text-xs overflow-x-auto max-h-40">
                            {JSON.stringify(testResult.raw, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-ivory-700 mb-1">Normalized Output</label>
                          <pre className="bg-ivory-50 p-3 rounded text-xs overflow-x-auto max-h-40">
                            {JSON.stringify(testResult.normalized, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMappingProfiles;
